import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  Star,
  Whatshot,
  Psychology,
  Diamond,
  TrendingUp,
  MovieFilter,
  EmojiEvents
} from '@mui/icons-material';

const USER_TITLES = {
  // Expertise Titles
  MOVIE_GURU: {
    name: 'Movie Guru',
    icon: MovieFilter,
    color: '#FF6B35',
    description: 'Master of cinema knowledge',
    requirement: 'Rate 500+ movies with high accuracy'
  },
  TREND_MASTER: {
    name: 'Trend Master',
    icon: TrendingUp,
    color: '#4ECDC4',
    description: 'Predicts viral content',
    requirement: 'Successfully predict 50+ trending movies'
  },
  HYPE_PROPHET: {
    name: 'Hype Prophet',
    icon: Psychology,
    color: '#45B7D1',
    description: 'Sees the future of entertainment',
    requirement: 'Discover 100+ movies before they trend'
  },

  // Social Titles
  COMMUNITY_LEADER: {
    name: 'Community Leader',
    icon: Star,
    color: '#FFD700',
    description: 'Leads and inspires the community',
    requirement: '10,000+ followers and high engagement'
  },
  TASTE_MAKER: {
    name: 'Taste Maker',
    icon: Star,
    color: '#FF69B4',
    description: 'Sets entertainment trends',
    requirement: 'Recommendations followed 1000+ times'
  },
  HYPE_HUNTER: {
    name: 'Hype Hunter',
    icon: Whatshot,
    color: '#FF4500',
    description: 'First to spot the next big thing',
    requirement: 'Consistently discover trending content early'
  },

  // Achievement Titles
  LEGEND: {
    name: 'Legend',
    icon: Diamond,
    color: '#9B59B6',
    description: 'Legendary status in the community',
    requirement: 'Unlock all major achievements'
  },
  CHAMPION: {
    name: 'Champion',
    icon: EmojiEvents,
    color: '#F39C12',
    description: 'Champion of entertainment discovery',
    requirement: 'Top 1% in multiple categories'
  }
};

const UserTitle = ({ titleType, size = 'medium', showDescription = false }) => {
  if (!titleType || !USER_TITLES[titleType]) return null;

  const title = USER_TITLES[titleType];
  const IconComponent = title.icon;

  const fontSize = {
    small: '0.7rem',
    medium: '0.8rem',
    large: '1rem'
  }[size];

  const iconSize = {
    small: 14,
    medium: 16,
    large: 20
  }[size];

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Chip
        icon={<IconComponent sx={{ fontSize: iconSize }} />}
        label={title.name}
        size={size === 'large' ? 'medium' : 'small'}
        sx={{
          bgcolor: `${title.color}20`,
          color: title.color,
          border: `1px solid ${title.color}40`,
          fontWeight: 600,
          fontSize,
          '& .MuiChip-icon': {
            color: title.color
          }
        }}
      />
      {showDescription && (
        <Typography variant="caption" color="text.secondary">
          {title.description}
        </Typography>
      )}
    </Box>
  );
};

// Component to display multiple titles
export const UserTitles = ({ titles = [], maxDisplay = 2, size = 'medium' }) => {
  if (!titles || titles.length === 0) return null;

  const displayTitles = titles.slice(0, maxDisplay);
  const remainingCount = titles.length - maxDisplay;

  return (
    <Box display="flex" gap={0.5} flexWrap="wrap" alignItems="center">
      {displayTitles.map((titleType, index) => (
        <UserTitle key={titleType} titleType={titleType} size={size} />
      ))}
      {remainingCount > 0 && (
        <Chip
          size={size === 'large' ? 'medium' : 'small'}
          label={`+${remainingCount}`}
          sx={{
            bgcolor: 'grey.200',
            color: 'grey.600',
            fontSize: size === 'small' ? '0.7rem' : '0.8rem'
          }}
        />
      )}
    </Box>
  );
};

export default UserTitle;